import React, { useMemo } from 'react';
import { Table } from '../../base/Table';
import { useParsedAcceptanceSlug } from '../../../services/queries/acceptanceData';
import FetchDataWrapper from '../../../../../components/FetchDataWrapper';
import { useFrozenValueUntil } from '../../../shared/hooks/frozenValueUntil';
import { FilterItemsBuilder } from '../services/filterItemsBuilder';
import { useRoomListViewData } from '../../../services/queries/acceptanceViewFlats';
import { presetKeys, presetKeysMap, presets } from '../constants/columns';
import { colRegistry } from './config';
import { createHouseAddress } from '../../../shared/packages/address';
import ExpandedContent from '../components/ExpandedContent';
import { useFilterConfig } from '../services/filterConfigCreator';
import { selectExpandedRowKeys } from '../utils/selectors';
import { usePageConfig } from '../services/pageConfigCreator';
import { StateProvider, useStateSelector } from '../stateManager';
import { stateKeys } from '../constants/state';
import { AcceptanceProvider } from '../services/acceptanceProvider';
import { AccessesProvider } from '../services/accessesProvider';
import { accessesKeys } from '../constants/accesses';
import { usePageSizeConfig } from '../services/pageSizeConfigCreator';

const dataTypes = { ...useRoomListViewData.dataTypes };

const initState = { [stateKeys.expandedRowKeys]: [] };

const RoomsTable = ({
  withCheckbox,
  preset,
  data,
  isFetching,
  refetch,
  rowSelectionConfig,
  acceptance,
  accesses,
}) => {
  const { acceptanceId, houseId } = useParsedAcceptanceSlug();
  const grouped = Boolean(acceptanceId && !houseId);

  const { value: page, onChange: onPageChange } = usePageConfig();
  const { value: pageSize, onChange: onPageChangeSize } = usePageSizeConfig();

  const frozenData = useFrozenValueUntil(
    { data, grouped, page, acceptance, pageSize },
    [data, grouped, page, acceptance, pageSize],
    !isFetching
  );

  return (
    <FetchDataWrapper
      data={frozenData.data}
      isFetching={isFetching}
      refetch={refetch}
      errMsg="Не удалось загрузить список помещений"
    >
      <AccessesProvider accesses={accesses}>
        <AcceptanceProvider acceptance={frozenData.acceptance}>
          <FilterItemsBuilder data={frozenData.data}>
            <StateProvider initialState={initState}>
              <Content
                data={frozenData.data}
                grouped={frozenData.grouped}
                preset={preset}
                withCheckbox={withCheckbox}
                rowSelectionConfig={rowSelectionConfig}
                page={frozenData.page}
                onPageChange={onPageChange}
                pageSize={frozenData.pageSize}
                onPageChangeSize={onPageChangeSize}
              />
            </StateProvider>
          </FilterItemsBuilder>
        </AcceptanceProvider>
      </AccessesProvider>
    </FetchDataWrapper>
  );
};

RoomsTable.dataTypes = dataTypes;
RoomsTable.presets = presetKeysMap;
RoomsTable.accessesKeys = accessesKeys;

export default RoomsTable;

function Content({
  data,
  grouped,
  preset,
  withCheckbox,
  rowSelectionConfig,
  page,
  onPageChange,
  pageSize,
  onPageChangeSize,
}) {
  const columns = useMemo(() => getColumns(preset), [preset]);

  const filterConfig = useFilterConfig();
  const expandedKeys = useStateSelector(selectExpandedRowKeys);

  return (
    <Table
      data={data}
      grouped={grouped}
      groupFn={groupRooms}
      columns={columns}
      withCheckbox={withCheckbox}
      renderExpanded={renderExpanded}
      filterConfig={filterConfig}
      rowSelectionConfig={rowSelectionConfig}
      rowExpansionConfig={{ value: expandedKeys }}
      pageConfig={{ value: page, onChange: onPageChange }}
      pageSizeConfig={{ value: pageSize, onChange: onPageChangeSize }}
    />
  );
}

function getColumns(preset = presetKeysMap.default) {
  const keys = presetKeys.includes(preset)
    ? presets[preset]
    : presets[presetKeysMap.default];

  return keys.map((key) => colRegistry[key]);
}

function groupRooms(rooms) {
  const _rooms = Array.isArray(rooms) ? rooms : [];

  const output = _rooms.reduce((map, room) => {
    let houseData = map.get(room.houseId);

    if (!houseData) {
      houseData = {
        name: createHouseAddress(room),
        rooms: [],
      };
      map.set(room.houseId, houseData);
    }

    houseData.rooms.push(room);
    return map;
  }, new Map());

  return output.size ? [...output.values()] : [];
}

function renderExpanded({ record, columns, withCheckbox }) {
  if (!record.relatedFlats?.length) return null;

  return (
    <ExpandedContent
      room={record}
      columns={columns}
      withCheckbox={withCheckbox}
    />
  );
}
